import { STOP_BAR_WIDTH } from 'products/stopbarproducts';
import { MM_PER_CM } from 'utils/constants';
import { sortNumbers, sumArray } from 'utils/mathUtil';

import { RailCalculations } from './RailCalculations';
import { DoorWidthEnum } from 'CoreAPI/Components/DoorWidthEnum';
import { RailTrackTypeEnum } from 'CoreAPI/Components/RailTrackTypeEnum';

export const DEFAULT_DOOR_LAYOUT_INDEX = 2;

export enum DoorLayoutStatusEnum {
  Recommended,
  GoodFit,
  IsRecommendedWithStopbars,
  CanBeRecommendedWithStopbars,
  ConsiderOpening,
  TooSmallOpening,
  ConsiderLargeOverlap,
  ConsiderSmallOverlap,
  RoomIsTooLarge,
  OneDoorTooMany,
  Unknown
}

/**
 * Business logic that calculates number of doors.
 *
 * Planning rules:
 * - Plan, if possible, with same door widths.
 * - Plan, if possible, doors in pairs.
 * - Put the widest door in the back, to access them easily.
 *
 */
export interface IDoorLayout {
  numThin: number;
  numWide: number;
  maxWidth: number;
}

export interface IPossibleDoorLayout extends IDoorLayout {
  index: number;
  opening: number;
  totalOverlap: number;
  isBest: boolean;
  types: DoorLayoutStatusEnum[];
  railTrack: RailTrackTypeEnum;
}

const DEFAULT_FILTER: DoorLayoutStatusEnum[] = [
  DoorLayoutStatusEnum.Recommended,
  DoorLayoutStatusEnum.CanBeRecommendedWithStopbars,
  DoorLayoutStatusEnum.IsRecommendedWithStopbars,
  DoorLayoutStatusEnum.ConsiderLargeOverlap,
  DoorLayoutStatusEnum.ConsiderOpening
];

const EXCLUDE_FILTER: DoorLayoutStatusEnum[] = [
  DoorLayoutStatusEnum.Unknown,
  DoorLayoutStatusEnum.RoomIsTooLarge,
  DoorLayoutStatusEnum.TooSmallOpening,
  DoorLayoutStatusEnum.OneDoorTooMany
];

export abstract class DoorCalculations {
  public static readonly thinDoorWidthMm = 771;
  public static readonly wideDoorWidthMm = 1021;
  public static readonly minimumOverlap = 25;
  public static readonly minimumGoodOverlapPerDoor = 150;
  public static readonly minimumGoodOpening = 700;
  public static readonly minimumOpening = 400; // TODO: Decide when to hide layouts based on opening size, and when to just show a warning.

  public static getDoorTypeWidthInMm(widthType: DoorWidthEnum) {
    switch (widthType) {
      case DoorWidthEnum.Thin:
        return DoorCalculations.thinDoorWidthMm;
      case DoorWidthEnum.Wide:
        return DoorCalculations.wideDoorWidthMm;
    }
  }

  public static getDoorTypeFromWidthInMm(widthTypeWidthInMm: number) {
    return widthTypeWidthInMm === DoorCalculations.thinDoorWidthMm ? DoorWidthEnum.Thin : DoorWidthEnum.Wide;
  }

  // There must probably be a better way to solve this than having to recalculate this every time...
  public static getAllDoorLayouts() {
    const layouts: IDoorLayout[] = [];
    const minNrOfDoors = 2;
    const maxNrOfDoors = 6;
    for (var numThin = 0; numThin <= maxNrOfDoors; numThin++) {
      for (var numWide = 0; numWide <= maxNrOfDoors; numWide++) {
        const totalNrOfDoors = numThin + numWide;
        const totalMinimumOverlap = (totalNrOfDoors - 1) * DoorCalculations.minimumOverlap;
        const totalWidth = numThin * DoorCalculations.thinDoorWidthMm + numWide * DoorCalculations.wideDoorWidthMm;
        const maxWidth = totalWidth - totalMinimumOverlap;
        if (totalNrOfDoors >= minNrOfDoors && totalNrOfDoors <= maxNrOfDoors) {
          layouts.push({
            numThin,
            numWide,
            maxWidth
          });
        }
      }
    }
    // Sort them
    layouts.sort((a, b) => {
      return a.maxWidth - b.maxWidth;
    });
    return layouts;
  }

  private static getStatusType(layout: IDoorLayout, roomWidth: number, selectedStopBar: boolean, maxOpening: number) {
    const overlapPerDoor = Math.round(DoorCalculations.getOverlapPerDoor(roomWidth, layout.numThin, layout.numWide, undefined, selectedStopBar));

    const totalNrOfDoors = layout.numThin + layout.numWide;
    const stopbarWidth = selectedStopBar ? STOP_BAR_WIDTH * 2 : 0;

    const totalMinimumOverlap = (totalNrOfDoors - 1) * DoorCalculations.minimumOverlap;

    const considerLargeOverlap = overlapPerDoor > DoorCalculations.minimumGoodOverlapPerDoor;

    const roomIsTooLarge = roomWidth > layout.maxWidth + totalMinimumOverlap + stopbarWidth;

    const considerSmallOverlap = overlapPerDoor <= DoorCalculations.minimumOverlap && !roomIsTooLarge;

    const canBeRecommended = roomWidth >= layout.maxWidth && roomWidth <= layout.maxWidth + STOP_BAR_WIDTH * 2;

    const recommended = roomWidth <= layout.maxWidth + stopbarWidth && !considerLargeOverlap;

    const totalDoorWidth =
      layout.numWide * DoorCalculations.getDoorTypeWidthInMm(DoorWidthEnum.Wide) +
      layout.numThin * DoorCalculations.getDoorTypeWidthInMm(DoorWidthEnum.Thin);

    const thinnestDoor =
      layout.numThin > 0 ? DoorCalculations.getDoorTypeWidthInMm(DoorWidthEnum.Thin) : DoorCalculations.getDoorTypeWidthInMm(DoorWidthEnum.Wide);

    const oneDoorTooMany = totalDoorWidth - thinnestDoor > roomWidth;
    const considerOpening = maxOpening < DoorCalculations.minimumGoodOpening;
    const tooSmallOpening = maxOpening < DoorCalculations.minimumOpening;

    const types: DoorLayoutStatusEnum[] = [];

    if (recommended) {
      types.push(canBeRecommended ? DoorLayoutStatusEnum.IsRecommendedWithStopbars : DoorLayoutStatusEnum.Recommended);
    } else if (canBeRecommended) {
      types.push(DoorLayoutStatusEnum.CanBeRecommendedWithStopbars);
    } else if (considerSmallOverlap) {
      types.push(DoorLayoutStatusEnum.ConsiderSmallOverlap);
    } else if (roomIsTooLarge) {
      types.push(DoorLayoutStatusEnum.RoomIsTooLarge);
    }

    if (considerLargeOverlap) {
      types.push(DoorLayoutStatusEnum.ConsiderLargeOverlap);
    }

    if (considerOpening) {
      types.push(DoorLayoutStatusEnum.ConsiderOpening);
    }

    if (tooSmallOpening) {
      types.push(DoorLayoutStatusEnum.TooSmallOpening);
    }

    if (oneDoorTooMany) {
      types.push(DoorLayoutStatusEnum.OneDoorTooMany);
    }

    return { types };
  }

  public static getDoorSetupForMaxOpening(layout: IDoorLayout, railTrackType: RailTrackTypeEnum) {
    const nrOfTracks = railTrackType === RailTrackTypeEnum.ThreeTrack ? 3 : 2;

    const tracks = Array.from({ length: nrOfTracks }, () => [0]);

    const { numThin, numWide } = layout;
    const wideDoorWidth = DoorCalculations.getDoorTypeWidthInMm(DoorWidthEnum.Wide);
    const thinDoorWidth = DoorCalculations.getDoorTypeWidthInMm(DoorWidthEnum.Thin);

    const doorWidths: number[] = new Array(numThin + numWide);
    doorWidths.fill(wideDoorWidth, 0, numWide);
    doorWidths.fill(thinDoorWidth, numWide, numWide + numThin);

    const filledTracks = doorWidths.reduce((newTracks, doorWidth) => {
      const tracksSorted = newTracks.sort((aTrack, bTrack) => {
        return sortNumbers(
          aTrack.reduce((a, b) => a + b, 0),
          bTrack.reduce((a, b) => a + b, 0)
        );
      });

      tracksSorted[0].push(doorWidth);

      return tracksSorted;
    }, tracks);

    return filledTracks
      .map((track) => track.filter(Boolean))
      .sort((a, b) => {
        // sort tracks based on total door width on each track
        return sumArray(a) - sumArray(b);
      });
  }

  public static getMaxOpening(layout: IDoorLayout, roomWidth: number, railTrackType: RailTrackTypeEnum) {
    const { numThin, numWide } = layout;
    const nrOfTracks = railTrackType === RailTrackTypeEnum.ThreeTrack ? 3 : 2;

    const wideDoorWidth = DoorCalculations.getDoorTypeWidthInMm(DoorWidthEnum.Wide);
    const thinDoorWidth = DoorCalculations.getDoorTypeWidthInMm(DoorWidthEnum.Thin);

    const tracks: number[] = new Array(nrOfTracks);
    tracks.fill(0);

    const doorWidths: number[] = new Array(numThin + numWide);
    doorWidths.fill(wideDoorWidth, 0, numWide);
    doorWidths.fill(thinDoorWidth, numWide, numWide + numThin);

    const tracksWithDoors = doorWidths.reduce((newTracks, doorWidth) => {
      newTracks.sort(sortNumbers)[0] += doorWidth;

      return newTracks;
    }, tracks);

    const widestTrack = tracksWithDoors.sort(sortNumbers)[tracksWithDoors.length - 1];

    return roomWidth - widestTrack;
  }

  public static getPossibleLayouts(roomWidth: number, selectedStopBar: boolean, selectedIndex?: number, selectedTrackType?: RailTrackTypeEnum, isThreeTrackAvailable: boolean = true) {
    const allLayouts = DoorCalculations.getAllDoorLayouts();
    let bestLayoutIndex: number | undefined;

    const possibleLayouts: IPossibleDoorLayout[] = allLayouts
      .map((layout, index) => {
        const stopbarWidth = selectedStopBar ? STOP_BAR_WIDTH * 2 : 0;
        const widthToFill = roomWidth - stopbarWidth;
        const isBest = bestLayoutIndex === undefined && layout.maxWidth >= widthToFill;
        if (isBest) {
          bestLayoutIndex = index;
        }

        const totalOverlap = DoorCalculations.getTotalOverlap(layout.numThin, layout.numWide, roomWidth, selectedStopBar);

        const shouldUseSelectedTrackType =
          index === selectedIndex && selectedTrackType !== undefined && isThreeTrackAvailable;

        const railTrackType: RailTrackTypeEnum =
          shouldUseSelectedTrackType
            ? (selectedTrackType as RailTrackTypeEnum)
            : RailCalculations.getRailTrackType(layout.numThin + layout.numWide, isThreeTrackAvailable);

        const maxOpening = DoorCalculations.getMaxOpening(layout, roomWidth, railTrackType);

        const { types } = DoorCalculations.getStatusType(layout, roomWidth, selectedStopBar, maxOpening);

        const possibleLayout: IPossibleDoorLayout = {
          ...layout,
          index: index,
          isBest: isBest,
          totalOverlap: totalOverlap,
          opening: maxOpening,
          types: types,
          railTrack: railTrackType
        };

        return possibleLayout;
      })
      // "Remove" unwanted suggestions here
      .filter(
        (layout) =>
          (layout.index === selectedIndex ||
            layout.isBest ||
            DEFAULT_FILTER.length === 0 ||
            layout.types.some((type) => DEFAULT_FILTER.includes(type))) &&
          !layout.types.some((type) => EXCLUDE_FILTER.includes(type))
      );

    return possibleLayouts;
  }

  // The layout that is closest do the room width is considered the best
  public static getBestLayoutIndex(roomWidth: number, considerStopbars?: boolean) {
    const allLayouts = DoorCalculations.getAllDoorLayouts();
    const widthToFill = considerStopbars ? roomWidth - STOP_BAR_WIDTH * 2 : roomWidth;
    return allLayouts.findIndex((layout) => layout.maxWidth >= widthToFill);
  }

  public static getDoorLayout(index: number) {
    const allLayouts = DoorCalculations.getAllDoorLayouts();
    return allLayouts[index];
  }

  public static calculateNumberOfDoors(roomWidth: number, considerStopbars?: boolean) {
    const bestLayoutIndex = DoorCalculations.getBestLayoutIndex(roomWidth, considerStopbars);
    const bestLayout = DoorCalculations.getDoorLayout(bestLayoutIndex);

    return {
      thin: bestLayout.numThin,
      wide: bestLayout.numWide
    };
  }

  /**
   * Calculates the overlap per door based on room width and the number of
   * doors that would be required to fill that gap.
   *
   * @param roomWidth
   * @returns Overlap in mm.
   */
  public static getOverlapPerDoor(
    roomWidth: number,
    numThinDoors: number,
    numWideDoors: number,
    numberOfInvalidOverlaps: number = 0,
    haveStopbars?: boolean
  ) {
    const numberOfOverlaps = numThinDoors + numWideDoors - 1 - numberOfInvalidOverlaps;
    if (numberOfOverlaps === 0) {
      return 0;
    }

    const totalOverlap = DoorCalculations.getTotalOverlap(numThinDoors, numWideDoors, roomWidth, haveStopbars);
    const overlapPerDoor = totalOverlap / numberOfOverlaps;

    return overlapPerDoor;
  }

  private static getTotalOverlap(numThinDoors: number, numWideDoors: number, roomWidth: number, haveStopbars?: boolean) {
    const totalDoorWidth = numThinDoors * DoorCalculations.thinDoorWidthMm + numWideDoors * DoorCalculations.wideDoorWidthMm;
    const stopBarWidth = haveStopbars ? STOP_BAR_WIDTH * 2 : 0;
    return totalDoorWidth - roomWidth + stopBarWidth;
  }

  public static logAllDoorLayoutsOptions() {
    const widthRange = { min: 150, max: 600 };
    for (let roomWidth = widthRange.min; roomWidth <= widthRange.max; roomWidth++) {
      const layouts = DoorCalculations.getPossibleLayouts(roomWidth * MM_PER_CM, false);

      console.log(roomWidth, layouts.length, layouts.filter((layout) => layout.types.includes(DoorLayoutStatusEnum.Recommended)).length);
    }
  }
}
